/**
 * Supabase client for CAP (Coverage Assessment Portal)
 */
import { createClient, SupabaseClient } from '@supabase/supabase-js';

import { envSwitch } from '@/utils/environmentUtils';

/**
 * SUPABASE_URL
 *
 * The URL of the Supabase project for each environment
 * SMS OTP is currently only enabled on the DEV project, so all environments point
 * to the same project until stage/prod SMS hooks are configured.
 *
 * These URLs are NOT secrets — they are public project identifiers
 */
const SUPABASE_URL = envSwitch({
  local: 'https://mvjhaljoxuqtemydjdxg.supabase.co',
  dev: 'https://mvjhaljoxuqtemydjdxg.supabase.co',
  qa: 'https://mvjhaljoxuqtemydjdxg.supabase.co',
  pr: 'https://mvjhaljoxuqtemydjdxg.supabase.co',
  stage: 'https://mvjhaljoxuqtemydjdxg.supabase.co',
  prod: 'https://mvjhaljoxuqtemydjdxg.supabase.co',
});

/**
 * SUPABASE_ANON_KEY
 *
 * The publishable (anon) key for the Supabase project.
 *
 * This is NOT a secret — it is designed to be public and embedded in client-side
 * code
 * See: https://supabase.com/docs/guides/api/api-keys
 */
const SUPABASE_ANON_KEY = envSwitch({
  local: 'sb_publishable_xqcbAJyd4CnxkJYnShpaDw_lU_5ScNq',
  dev: 'sb_publishable_xqcbAJyd4CnxkJYnShpaDw_lU_5ScNq',
  qa: 'sb_publishable_xqcbAJyd4CnxkJYnShpaDw_lU_5ScNq',
  pr: 'sb_publishable_xqcbAJyd4CnxkJYnShpaDw_lU_5ScNq',
  stage: 'sb_publishable_xqcbAJyd4CnxkJYnShpaDw_lU_5ScNq',
  prod: 'sb_publishable_xqcbAJyd4CnxkJYnShpaDw_lU_5ScNq',
});

// Singleton — created once and reused to avoid opening multiple connections.
let client: SupabaseClient | null = null;

/**
 * Returns the Supabase client instance, creating it on first call.
 *
 * Auth options:
 *   persistSession    — Store the session in localStorage so it survives page reloads.
 *   autoRefreshToken  — Automatically refresh the JWT before it expires.
 *   detectSessionInUrl — Supabase reads auth tokens from the URL hash on page load.
 *                        Enabled for the email magic link flow which redirects back
 *                        with #access_token in the URL fragment.
 */
export const getSupabaseClient = (): SupabaseClient => {
  if (!client) {
    client = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
      },
    });
  }

  return client;
};

/**
 * Resets the client instance. Used in tests to get a fresh client between test cases.
 */
export const resetSupabaseClient = (): void => {
  client = null;
};
